import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useLocation } from "react-router-dom";

import { ArrowheadRightDarkIcon, CompleteBlueIcon } from "../../../../assets";
import { ProjectGroupPermission, ResourcePermissionConstants, UserType } from "../../../../constants";
import { TaskConstants } from "../../../../constants/workflow/TaskConstants";
import { CursorChangeProps, ResultType, SelectData, TaskPanelData } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import {
  GetTaskBoardResponse,
  searchWorkflowTasks,
  SearchWorkflowTasksRequest,
  SearchWorkflowTasksResponse,
} from "../../../../service/query";
import { FilterGroups, ResultData } from "../../../../service/Shared";
import { useTaskTagsFilter } from "../../../../templates/TasksGeneralView";
import { useDeepLinkedTask, useTaskPanelWorkflow } from "../../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../../useAuth";
import { flattenObject, useUpdateEffect } from "../../../../utils";
import { getProjectDetailsByUuid, getWorkflowTasksBoardRoute } from "../../../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  DataGridCursorDataLoadEventHandler,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
} from "../../../../widget";
import { AvatarCellFormatter } from "../../../../widget/data/DataGrid/components/CellFormatters";
import { IconCellFormatterData, IconLinkCellFormatterData } from "../../../../widget/data/DataGrid/models";
import { LandholdingContext } from "../LandholdingContext";

interface UseTasksReturnData {
  defaultSortingCriteria: SortColumn[];
  isTasksLoading: boolean;
  tasksOnChange: DataGridCursorDataLoadEventHandler;

  workflow: GetTaskBoardResponse["results"][number] | undefined;

  selectedCompleteTask: TaskPanelData | undefined;
  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;

  showCompleteTaskModal: boolean;
  handleCloseCompleteTaskModal: () => void;

  showWorkflowTaskPanel: boolean;
  handleCloseWorkflowTaskPanel: () => void;
  // Create-mode task type: true when the user chose to create a general task in the New task modal.
  createTaskIsGeneral: boolean;
  showNewTaskModal: boolean;
  setShowNewTaskModal: Dispatch<SetStateAction<boolean>>;
  handleNewTask: (isGeneral: boolean) => void;

  // A landholding has no workflow of its own, so only a general task is creatable here.
  canCreateGeneralTask: boolean;

  landholdingName: string | undefined;
  landholdingGroupUuid: string | undefined;

  tagsFilterSelectData: SelectData;
  tagsFilterValues: string[];
  onTagsFilter: (keys: string[]) => void;

  handleRefresh: () => void;
  refreshTasks: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[];
  currentUserType: UserType;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
  {
    name: "Task",
    key: "taskLink",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    filterable: true,
    minWidth: 340,
    sortable: true,
  },
  {
    key: "projectLink",
    name: "Project",
    dataType: "string",
    formatter: "link",
    alignment: "left",
    filterable: true,
    minWidth: 140,
    sortable: true,
  },
  {
    name: "Assigned to",
    key: "avatar",
    dataType: "string",
    formatter: "custom",
    alignment: "center",
    customCellFormatter: AvatarCellFormatter,
    filterable: true,
    minWidth: 140,
    sortable: true,
  },
  {
    key: "status",
    name: "Status",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
    minWidth: 140,
  },
  {
    key: "deadlineDate",
    name: "Deadline",
    dataType: "Date",
    formatter: "dateOnlyWithIndicator",
    alignment: "left",
    minWidth: 140,
  },
  {
    key: "updatedAt",
    name: "Date updated",
    dataType: "Date",
    formatter: "dateOnly",
    filterable: false,
    minWidth: 140,
  },
  {
    name: "Complete",
    key: "completeIcon",
    dataType: "boolean",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 100,
  },
  {
    key: "viewTasksBoardIcon",
    name: "Tasks board",
    dataType: "string",
    formatter: "iconLink",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 100,
  },
];

const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];

export const useTasks = (): UseTasksReturnData => {
  const location = useLocation();

  const { landholdingDetails, readGroupProjectsPermission } = useContext(LandholdingContext);
  const { currentUserType, hasPermission } = useAuth();

  const [selectedCompleteTask, setSelectedCompleteTask] = useState<TaskPanelData>();
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();

  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);
  const [showCompleteTaskModal, setShowCompleteTaskModal] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [createTaskIsGeneral, setCreateTaskIsGeneral] = useState(false);

  const [isTasksLoading, setIsTasksLoading] = useState(true);

  const [refreshTasks, setRefreshTasks] = useState(false);

  const landholdingGroupUuid = landholdingDetails?.groupGuid;

  /*
    Mirror the API's general-task gate (GeneralTaskCreationStrategy): createResource on the creating org,
    plus read access to the parent — here the landholding group rather than a project. A workflow-step task
    needs a workflow to hang its step off and a landholding has none, so it is never offered.
  */
  const canCreateGeneralTask =
    hasPermission(ResourcePermissionConstants.CREATE_RESOURCE) &&
    readGroupProjectsPermission !== undefined &&
    readGroupProjectsPermission !== ProjectGroupPermission.NONE;

  const handleRefresh = useCallback((): void => {
    setRefreshTasks((prev) => !prev);
  }, []);

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setCreateTaskIsGeneral(false);
    setShowWorkflowTaskPanel(false);
  }, []);

  /*
    The grid returns no rows until the landholding is known (its uuid scopes every search), so reload once
    it arrives — and again if the landholding changes in place, since the tab stays mounted across that.
  */
  useUpdateEffect(() => setRefreshTasks((prev) => !prev), [landholdingGroupUuid]);

  // Confirming the New task modal opens the overlay in create mode for the chosen task type.
  const handleNewTask = useCallback((isGeneral: boolean): void => {
    setShowNewTaskModal(false);
    setSelectedPanelTask(undefined);
    setCreateTaskIsGeneral(isGeneral);
    setShowWorkflowTaskPanel(true);
  }, []);

  /*
    A notification for a task links to this tab naming the task in ?taskUuid= (KAN-9609), so the user
    lands on the task itself rather than having to find its row in the grid.
  */
  const deepLinkedTask = useDeepLinkedTask();
  useEffect(() => {
    if (!deepLinkedTask) return;
    setSelectedPanelTask(deepLinkedTask);
    setShowWorkflowTaskPanel(true);
  }, [deepLinkedTask]);

  const workflow = useTaskPanelWorkflow(selectedPanelTask?.workflowUuid);

  // refreshTasks doubles as the tag-filter refresh key: saving a task can create new org-level tags,
  // which have to appear in the filter alongside the reloaded grid.
  const { tagsFilterSelectData, tagsFilterValues, tagTaskUuids, onTagsFilter } = useTaskTagsFilter(
    handleRefresh,
    refreshTasks
  );

  const handleCloseCompleteTaskModal = (): void => {
    setSelectedCompleteTask(undefined);
    setShowCompleteTaskModal(false);
  };

  // --- For upcoming tasks data grid ---
  const formatTasksData = useCallback((responseData: SearchWorkflowTasksResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData & { avatar: { fullName?: string } };
        result.avatar = { fullName: d.assignee?.name };
        result.taskLink = {
          text: d.name,
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        /*
          The project a task belongs to is the workflow's object, not the task's own object — a general task
          created against the landholding has no workflow, so it has no project to link to and the cell stays
          empty.
        */
        if (d.workflowObjectUuid) {
          result.projectLink = {
            text: d.workflowObjectName,
            to: getProjectDetailsByUuid(d.workflowObjectUuid, currentUserType),
            state: { origin: { goBackText: "Back to landholding", from: location.pathname } as IStateOrigin },
          } as DataGridLinkCellFormatterData;
        }

        // A general task has no workflow, so there is no tasks board to link to.
        if (d.workflowUuid) {
          result.viewTasksBoardIcon = {
            to: getWorkflowTasksBoardRoute(d.workflowUuid, currentUserType),
            state: { origin: { goBackText: "Back to landholding", from: location.pathname } as IStateOrigin },
            icon: <ArrowheadRightDarkIcon />,
          } as IconLinkCellFormatterData;
        }

        if (d.status !== TaskConstants.TASK_STATUS_UNASSIGNED)
          result.completeIcon = {
            action: () => {
              setSelectedCompleteTask({
                deadlineDate: d.deadlineDate,
                isMandatory: d.isMandatory,
                isMilestone: d.isMilestone,
                keyDate: d.keyDate,
                status: d.status,
                repeat: d.repeat,
                description: d.description,
                name: d.name,
                visibility: [],
                assignedToUserUuid: d.assignee?.uuid ?? null,
                assignedToUserName: d.assignee?.name ?? null,
                ownerOrganisationUuid: d.ownerOrganisation.uuid,
                relateToActivity: d.objectType === "Activity",
                activityUuid: d.objectType === "Activity" ? d.objectUuid : null,
                objectType: d.objectType,
                objectUuid: d.objectUuid,
                stepUuid: d.step?.uuid ?? null,
                phase: d.step?.phase ?? null,
                uuid: d.uuid,
                rowVersion: d.rowVersion,
                resolution: d.resolution,
                reminderDate: d.reminderDate,
                reminderType: d.reminderType,
                addReminder: d.reminderDate != null,
              });
              setShowCompleteTaskModal(true);
            },
            icon: <CompleteBlueIcon width={20} height={20} strokeWidth={1.5} />,
          } as IconCellFormatterData;

        return result;
      }) || []
    );
  }, []);

  const tasksOnChange: DataGridCursorDataLoadEventHandler = async ({
    paging,
    filtering,
    sorting,
  }: CursorChangeProps) => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    if (landholdingGroupUuid === undefined) {
      return data;
    }

    setIsTasksLoading(true);

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    // Tag filter: restrict to the WorkflowTask uuids linked to the selected tags.
    if (tagTaskUuids !== undefined) {
      filterCriteria.uuid = { operator: "in", value: tagTaskUuids };
    }

    if (filterCriteria.taskLink) {
      filterCriteria.name = filterCriteria.taskLink;
      filterCriteria.taskLink = undefined;
      delete filterCriteria.taskLink;
    }

    if (filterCriteria.projectLink) {
      filterCriteria.workflowObjectName = filterCriteria.projectLink;
      delete filterCriteria.projectLink;
    }

    if (filterCriteria.avatar) {
      filterCriteria.assignee = {
        name: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }

    const sortKeyMap: Record<string, string> = {
      taskLink: "name",
      avatar: "assignee.name",
      projectLink: "workflowObjectName",
    };

    const request: SearchWorkflowTasksRequest & FilterGroups<SearchWorkflowTasksRequest["filter"]> = {
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
      },
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: sorting.map((s: { key: any; direction: any }) => ({
        key: sortKeyMap[s.key] || s.key,
        direction: s.direction,
      })),
      filter: { results: filterCriteria },
      /*
        A task belongs to this landholding either via its workflow (a workflow task carries the group it
        belongs to on `groups`) or directly (a general task created here has no workflow and hangs off the
        landholding through objectType/objectUuid), so match on either.
      */
      filterGroups: [
        {
          operator: "or",
          filter: {
            results: {
              groups: { some: { groupUuid: { operator: "eq", value: landholdingGroupUuid } } },
              objectUuid: { operator: "eq", value: landholdingGroupUuid },
            },
          },
        },
      ],
    };

    await searchWorkflowTasks(request)
      .then((response) => {
        data = {
          resultData: formatTasksData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setIsTasksLoading(false);
      });
    return data;
  };

  return {
    columns,
    refreshTasks,
    handleRefresh,

    selectedCompleteTask,
    showCompleteTaskModal,
    handleCloseCompleteTaskModal,

    selectedPanelTask,
    showWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    createTaskIsGeneral,
    showNewTaskModal,
    setShowNewTaskModal,
    handleNewTask,

    canCreateGeneralTask,

    landholdingName: landholdingDetails?.groupName,
    landholdingGroupUuid,

    tagsFilterSelectData,
    tagsFilterValues,
    onTagsFilter,

    workflow,
    isTasksLoading,
    tasksOnChange,
    defaultSortingCriteria,
    currentUserType,
  };
};
